import { model, Model, createThunk } from 'mst-collection';
import { castToSnapshot, getRoot, types } from 'mobx-state-tree';

import { useRoot } from '../useRoot';
import { PostListModel } from './PostListModel';
import {
  Api,
  schemas,
  IPostResponse,
  IUserPublicResponse,
  ICreatePostParams,
} from '@api';
import { trim } from '@utils';
import { UploadMediaListModel } from './UploadMediaListModel';
import { RootModelType } from '../RootModel';

export class Posts extends Model({
  list: types.optional(PostListModel, {}),
  uploadMediaList: types.optional(UploadMediaListModel, {}),
}) {
  create = createThunk(
    (
      params: Pick<
        ICreatePostParams,
        'text' | 'photoIds' | 'videoId'
      >,
    ) => {
      return async function create(this: Posts) {
        let text;

        if (params.text) {
          text = trim(params.text, true);
        }

        const res = await Api.Feed.createPost({
          ...params,
          text,
          sendTime: new Date().toISOString(),
        });

        const post = await Api.Feed.getPostById({
          id: res.data.id,
        });

        const currentUser =
          getRoot<RootModelType>(this).viewer.currentUser;

        this.list.addToBegin(
          castToSnapshot({ ...post.data, user: currentUser }),
        );
      };
    },
  );

  fetchUsersAndMerge = createThunk(
    (postsResponse: IPostResponse[]) => {
      return async function fetchUsersAndMerge(flow) {
        const userIds = postsResponse
          .map((t) => t.userId)
          .filter((id) => typeof id === 'number') as number[];

        const usersResponse = await Api.Profile.getSharedData(
          userIds,
        );

        const normalizedUsers = flow.normalize(
          usersResponse.data,
          schemas.UserListSchema,
        );

        return postsResponse.map((post) => {
          if (typeof post.userId !== 'number') {
            return post;
          }

          return {
            ...post,
            user: normalizedUsers.entities.users[
              post.userId
            ] as IUserPublicResponse,
          };
        });
      };
    },
  );
}

export const PostsModel = model(Posts);

export function usePostsModel() {
  const root = useRoot();

  return root.posts;
}
